import { useEffect, useState } from "react";
import QuestionBox from "../../ui/QuestionBox/QuestionBox";
import RadioButton from "../../ui/RadioButton/RadioButton";
import classes from "../Infrastructure/Infrastructure.module.css";
import QuestionCard from "../../ui/QuestionCard/QuestionCard";
import CommonFooter from "../../CommonFooter/CommonFooter";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { env } from "../../../constants/env";
import { Bounce, toast } from "react-toastify";
import { useParams } from "react-router-dom";
import CommonButton from "../../ui/CommonButton/CommonButton";

const initialState = {
  hasQMS: "",
  hasQualityOfficer: "",
  qualityOfficerName: "",
  qmsToolOrStandard: "",
  hasInternalQualityControls: "",
  enrolledInEQA: "",
  eqaPrograms: ["", ""],
};

const QualityManagementSystem = () => {
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState(["", ""]);
  const { user } = useAuth();

  const { labId: paramLabId } = useParams();
  const labId = paramLabId || user?.labId;

  const userRole = user?.role;

  const [formData, setFormData] = useState(initialState);
  const [ownerLabId, setOwnerLabId] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const isReadOnly = userRole !== "LABORATORY" || !editMode;

  const sectionName = "quality-management"; // formType

  useEffect(() => {
    if (labId) {
      fetchQuestionnaire();
    }
  }, [labId]);

  const fetchQuestionnaire = async () => {
    try {
      setIsFetching(true);
      const res = await axios.get(
        `${env.kataApiUrl}/api/questionnaire/${labId}/${sectionName}`
      );

      if (res.data) {
        if (res.data.answers) {
          setFormData(res.data.answers);
          setEditMode(false); // ✅ answers exist → read-only by default
        } else {
          setFormData(initialState);
          setEditMode(true); // ✅ no answers → open in edit mode
        }
        setOwnerLabId(res.data.labId);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log("No existing questionnaire found for this lab.");
        setFormData(initialState);
        setEditMode(true); // ✅ new lab → start in edit mode
      } else {
        console.error("Error fetching questionnaire:", error);
        toast.error("Failed to load saved questionnaire");
      }
    } finally {
      setIsFetching(false);
    }
  };

  // --- pass disabled to inputs ---
  const handleChange = (field, value) => {
    if (isReadOnly) return; // block edits
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (section, key, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  const handleDone = async () => {
    try {
      setLoading(true);
      const payload = {
        labId,
        formType: sectionName,
        answers: formData,
      };

      const res = await axios.post(
        `${env.kataApiUrl}/api/questionnaire/${sectionName}`,
        payload
      );

      toast.success("Submitted successfully!");

      fetchQuestionnaire();
      setCurrentSet(0);
       setEditMode(false); // exit edit mode

      console.log(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const questionSets = [
    {
      content: (
        <div className="d-flex flex-column gap-4">
          <QuestionBox
            number={1}
            question="Is there Quality Management System (QMS) in place or being put in place at the Laboratory?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="hasQMS"
                checked={formData.hasQMS === "Yes"}
                onChange={() => handleChange("hasQMS", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasQMS"
                checked={formData.hasQMS === "No"}
                onChange={() => handleChange("hasQMS", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={2}
            question="Is there a Quality Officer at the Laboratory?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="hasQualityOfficer"
                checked={formData.hasQualityOfficer === "Yes"}
                onChange={() => handleChange("hasQualityOfficer", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasQualityOfficer"
                checked={formData.hasQualityOfficer === "No"}
                onChange={() => handleChange("hasQualityOfficer", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={3}
            question="Name of the Quality Officer?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <div
                className={`${classes.customInput} d-flex align-items-center`}
              >
                <input
                  type="text"
                  placeholder="Enter name"
                  onChange={(e) =>
                    handleChange("qualityOfficerName", e.target.value)
                  }
                  value={formData.qualityOfficerName}
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </QuestionBox>
          <QuestionBox
            number={4}
            question="Which tool or standard is being applied at the Laboratory?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <div
                className={`${classes.customInput} d-flex align-items-center`}
              >
                <input
                  type="text"
                  placeholder="Enter name"
                  onChange={(e) =>
                    handleChange("qmsToolOrStandard", e.target.value)
                  }
                  value={formData.qmsToolOrStandard}
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </QuestionBox>
          <QuestionBox
            number={5}
            question="Is there internal Quality Controls in use at the Laboratory?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasInternalQualityControls"
                checked={formData.hasInternalQualityControls === "Yes"}
                onChange={() =>
                  handleChange("hasInternalQualityControls", "Yes")
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasInternalQualityControls"
                checked={formData.hasInternalQualityControls === "No"}
                onChange={() =>
                  handleChange("hasInternalQualityControls", "No")
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={6}
            question="Is the laboratory enrolled in External Quality Assessment (EQA) programs?"
            singleInput
            answers={formData.eqaPrograms}
            onAnswersChange={(newAnswers) =>
              handleChange("eqaPrograms", newAnswers)
            }
            disabled={isReadOnly}
          />
        </div>
      ),
    },
  ];

  const { subtitle, content } = questionSets[currentSet];

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
     <div
      className={`${classes.wrapper} ${
        paramLabId ? classes.fromParams : ""
      } ${isReadOnly ? classes.readOnly : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <h2>Quality Management System</h2>
        {userRole === "LABORATORY" && !editMode && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditMode(true)}
            title="Edit"
          />
        )}
      </div>
      {(!editMode && userRole !== "MINISTRY") ? (
        <div className={classes.infoText}>
          ✅ Your Quality Management System has been submitted.
        </div>
      ) : (
        content
      )}
      {!isReadOnly && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => {
            setEditMode(false);
            fetchQuestionnaire();
            setCurrentSet(0);
          }}
          questionSets={questionSets}
          currentSet={currentSet}
          loading={loading}
          doneText={loading ? "Submitting..." : "Submit"}
        />
      )}
    </div>
  );
};

export default QualityManagementSystem;
